/**
 * Word-level diff for the report writer's AI edit review.
 *
 * The AI returns a revised copy of the writer's text; this turns the pair into
 * a list of hunks the writer can accept or reject one at a time, then rebuilds
 * the text from those decisions. Tokens are words, runs of whitespace, and
 * single punctuation marks so a fixed comma or a changed word shows on its own
 * rather than as a whole rewritten sentence.
 */

export type DiffOp = { type: 'equal' | 'insert' | 'delete'; text: string };

export interface DiffHunk {
  id: number;
  /** Text present in the original that the suggestion removes ('' for a pure insertion). */
  removed: string;
  /** Text the suggestion adds ('' for a pure deletion). */
  added: string;
}

export type DiffSegment =
  | { kind: 'equal'; text: string }
  | { kind: 'hunk'; hunk: DiffHunk };

const TOKEN_PATTERN = /\s+|[\p{L}\p{N}'’-]+|[^\s\p{L}\p{N}]/gu;

export const tokenize = (text: string): string[] => text.match(TOKEN_PATTERN) ?? [];

/**
 * Longest-common-subsequence diff over tokens. Texts here are a few thousand
 * tokens at most (the API caps input at 8000 characters), so a plain DP table
 * is fast enough and easier to trust than a Myers implementation.
 */
export const diffTokens = (a: string[], b: string[]): DiffOp[] => {
  // Strip a shared prefix and suffix first; edits are usually local.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const n = midA.length;
  const m = midB.length;

  const ops: DiffOp[] = [];
  const pushOp = (type: DiffOp['type'], text: string) => {
    const last = ops[ops.length - 1];
    if (last && last.type === type) last.text += text;
    else ops.push({ type, text });
  };

  for (let i = 0; i < start; i += 1) pushOp('equal', a[i]);

  if (n === 0 || m === 0) {
    for (const t of midA) pushOp('delete', t);
    for (const t of midB) pushOp('insert', t);
  } else {
    // lcs[i][j] = LCS length of midA[i..] and midB[j..]
    const width = m + 1;
    const lcs = new Uint32Array((n + 1) * width);
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = m - 1; j >= 0; j -= 1) {
        lcs[i * width + j] = midA[i] === midB[j]
          ? lcs[(i + 1) * width + j + 1] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
      }
    }

    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (midA[i] === midB[j]) {
        pushOp('equal', midA[i]);
        i += 1;
        j += 1;
      } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
        pushOp('delete', midA[i]);
        i += 1;
      } else {
        pushOp('insert', midB[j]);
        j += 1;
      }
    }
    while (i < n) { pushOp('delete', midA[i]); i += 1; }
    while (j < m) { pushOp('insert', midB[j]); j += 1; }
  }

  for (let i = endA; i < a.length; i += 1) pushOp('equal', a[i]);

  return ops;
};

/**
 * Group adjacent non-equal ops into hunks. Whitespace-only equal runs between
 * two changes are folded into the hunk so "word, word" edits read as one
 * decision instead of three.
 */
export const buildSegments = (original: string, revised: string): DiffSegment[] => {
  const ops = diffTokens(tokenize(original), tokenize(revised));
  const segments: DiffSegment[] = [];
  let nextId = 0;
  let pending: { removed: string; added: string } | null = null;

  const flush = () => {
    if (!pending) return;
    if (pending.removed || pending.added) {
      segments.push({ kind: 'hunk', hunk: { id: nextId, removed: pending.removed, added: pending.added } });
      nextId += 1;
    }
    pending = null;
  };

  for (let index = 0; index < ops.length; index += 1) {
    const op = ops[index];
    if (op.type === 'equal') {
      const next = ops[index + 1];
      const isBridge = pending && next && next.type !== 'equal' && op.text.trim() === '';
      if (isBridge && pending) {
        pending.removed += op.text;
        pending.added += op.text;
        continue;
      }
      flush();
      const last = segments[segments.length - 1];
      if (last && last.kind === 'equal') last.text += op.text;
      else segments.push({ kind: 'equal', text: op.text });
    } else {
      if (!pending) pending = { removed: '', added: '' };
      if (op.type === 'delete') pending.removed += op.text;
      else pending.added += op.text;
    }
  }
  flush();

  return segments;
};

/** Rebuild the text from the writer's per-hunk decisions. */
export const applyDecisions = (segments: DiffSegment[], accepted: ReadonlySet<number>): string =>
  segments
    .map((segment) => {
      if (segment.kind === 'equal') return segment.text;
      return accepted.has(segment.hunk.id) ? segment.hunk.added : segment.hunk.removed;
    })
    .join('');

export const countHunks = (segments: DiffSegment[]): number =>
  segments.filter((segment) => segment.kind === 'hunk').length;
